import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { PaginatedResponse } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { paloIsRefused } from '@synergy/components-palo';

import { Api } from '../../../../api';
import { AppState } from '../../../../store/models/app-state';
import { CompleteInvoicesResponse } from '../../invoice-api';
import { ExportInvoice } from '../models/export-invoice';
import { ExportInvoicesRequest } from '../models/export-invoices-request';
import { Invoice } from '../../models/invoice';
import { LockedPartner, noticeLockedPartners } from '../../../../common/utils/locked-partner-notice';
import { Prefix } from '../../../../common/models/prefix';
import { ReduxDispatch } from '../../../../store/use-thunk-dispatch';

export const DELETE_INVOICES = 'DELETE_INVOICES';
export const COMPLETE_INVOICES_SUCCESS = 'COMPLETE_INVOICES_SUCCESS';
export const DUPLICATE_INVOICE = 'DUPLICATE_INVOICE';
export const DUPLICATED_INVOICE = 'DUPLICATED_INVOICE';
export const RECEIVE_INVOICES = 'RECEIVE_INVOICES';
export const REQUEST_INVOICE_END = 'REQUEST_INVOICE_END';
export const REQUEST_INVOICE_START = 'REQUEST_INVOICE_START';
export const REQUEST_INVOICES = 'REQUEST_INVOICES';
export const RESET_INVOICES_STATE = 'RESET_INVOICES_STATE';
export const EXPORT_INVOICES = 'EXPORT_INVOICES';
export const EXPORT_INVOICES_SUCCESS = 'EXPORT_INVOICES_SUCCESS';
export const REQUEST_LATEST_EXPORT = 'REQUEST_LATEST_EXPORT';
export const RECEIVE_LATEST_EXPORT = 'RECEIVE_LATEST_EXPORT';
export const CANCEL_INVOICE = 'CANCELED_INVOICE';
export const CANCELED_INVOICE = 'CANCELED_INVOICE';
export const RESET_LATEST_EXPORT = 'RESET_LATEST_EXPORT';

export const resetInvoicesState = () => {
    return {
        type: RESET_INVOICES_STATE,
    };
};

export const receiveInvoices = (invoices: PaginatedResponse<Invoice>) => {
    return {
        type: RECEIVE_INVOICES,
        invoices,
    };
};

const receiveLatestExport = (latestExport: ExportInvoice) => {
    return {
        type: RECEIVE_LATEST_EXPORT,
        latestExport,
    };
};

export const fetchInvoices = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: REQUEST_INVOICE_START });
        dispatch({ type: REQUEST_INVOICES });

        const response = await api.invoices.cget(searchParams);
        if (response.success) {
            dispatch({ type: RECEIVE_INVOICES, invoices: response.result });
            dispatch({ type: REQUEST_INVOICE_END });
        } else {
            // Reset the loading flag on failure too, otherwise the list spinner
            // stays stuck and reappears on the next navigation to the list.
            dispatch({ type: REQUEST_INVOICE_END });
            // The list says a refused filter in its own body, with the cause
            // and a way out; the toast would say it again without either.
            if (!paloIsRefused(searchParams.toString())) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            }
        }
    };
};

export const duplicateInvoice = (
    id: number,
    partners: (LockedPartner | undefined)[] = [],
    searchParams?: URLSearchParams
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DUPLICATE_INVOICE });

        const response = await api.invoices.duplicate(id);
        dispatch({ type: REQUEST_INVOICE_END });

        if (response.success) {
            noticeLockedPartners(dispatch, partners);

            // The copy is a draft, and the list it was made from may well be
            // showing something else — duplicating out of "Abgeschlossen" put
            // a record with status "Erfasst" at the top of it (#2209, O-7).
            // Asked again with the filter that is on, the list answers for
            // itself; only a caller that cannot say what is filtered falls
            // back to putting the copy in front.
            if (searchParams) {
                await (dispatch as ReduxDispatch)(fetchInvoices(searchParams));
            } else {
                dispatch({ type: DUPLICATED_INVOICE, invoice: response.result });
            }
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const cancelInvoice = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const result = await api.invoices.cancel(id);
        dispatch({ type: CANCEL_INVOICE });

        if (result.success) {
            dispatch({ type: CANCELED_INVOICE, invoiceId: id });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
        }
    };
};

export const exportInvoices = (request: ExportInvoicesRequest) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: EXPORT_INVOICES });

        const response = await api.invoices.export(request);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.info('app.snackbar.invoiceExportPolling')));
            dispatch({ type: EXPORT_INVOICES_SUCCESS });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchLatestExport = (prefix: Prefix) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: REQUEST_LATEST_EXPORT });
        const response = await api.invoices.getLatestExport(prefix);
        if (response.success) {
            dispatch(receiveLatestExport(response.result));
        }
    };
};

export const resetLatestExport = () => {
    return {
        type: RESET_LATEST_EXPORT,
    };
};

export const deleteInvoices = (ids: number[]) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DELETE_INVOICES, ids });
        const response = await api.invoices.delete(ids);

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.invoiceDeletedSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const completeInvoices = (ids: number[]) => {
    return async (
        dispatch: Dispatch,
        _getState: () => AppState,
        { api }: { api: Api }
    ): Promise<CompleteInvoicesResponse | undefined> => {
        const response = await api.invoices.complete(ids);

        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));

            return undefined;
        }

        const { completed, skipped } = response.result;

        dispatch({ type: COMPLETE_INVOICES_SUCCESS, results: completed });

        // A batch may do part of its job (#2136). "Erfolgreich" over a run that
        // skipped half the selection is the one message this must not send; the
        // caller shows which invoices were left and why.
        if (completed.length > 0) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.invoiceCompletedSuccess')));
        }

        return { completed, skipped };
    };
};
